import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Code, Smartphone, Globe, Server, CheckCircle, Target, Zap, Layers, Database } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CursorGlow from '../../components/CursorGlow';
import ScrollProgress from '../../components/ScrollProgress';

const WebPortfolio = () => {
  const project = {
    title: 'FinTrack - Finansal Dashboard',
    client: 'FinTrack Fintech',
    duration: '16 Hafta',
    category: 'Web Uygulaması',
    year: '2025',
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop',
    description: 'FinTrack için kapsamlı bir finansal yönetim dashboard\'u geliştirdik. React ve Node.js ile modern bir mimari kullanarak, gerçek zamanlı veri görselleştirme ve analitik araçları sunduk.',
    challenge: 'FinTrack, kullanıcılarına karmaşık finansal verileri basit ve anlaşılır şekilde sunmak istiyordu. Mevcut sistem yavaş ve kullanıcı dostu değildi.',
    solution: 'Modern web teknolojileri ile performanslı ve responsive bir dashboard geliştirdik. WebSocket ile gerçek zamanlı veri akışı sağladık.'
  };

  const stats = [
    { label: 'Sayfa Yüklenme', value: '0.8s', icon: Zap },
    { label: 'Lighthouse Skoru', value: '98', icon: Target },
    { label: 'API Endpoint', value: '45+', icon: Server },
    { label: 'Aktif Kullanıcı', value: '15K+', icon: Globe }
  ];

  const techStack = [
    { category: 'Frontend', techs: ['React 18', 'TypeScript', 'Tailwind CSS', 'Recharts', 'React Query'] },
    { category: 'Backend', techs: ['Node.js', 'Express', 'PostgreSQL', 'Redis', 'WebSocket'] },
    { category: 'DevOps', techs: ['Docker', 'Kubernetes', 'AWS', 'CI/CD', 'Monitoring'] },
    { category: 'Tools', techs: ['Figma', 'Git', 'Jest', 'Cypress', 'Storybook'] }
  ];

  const features = [
    { title: 'Gerçek Zamanlı Dashboard', desc: 'WebSocket ile anlık veri güncellemeleri' },
    { title: 'Gelişmiş Grafikler', desc: 'İnteraktif ve özelleştirilebilir veri görselleştirme' },
    { title: 'Raporlama Modülü', desc: 'PDF ve Excel formatında otomatik rapor oluşturma' },
    { title: 'Kullanıcı Yönetimi', desc: 'Rol bazlı erişim kontrolü ve team yönetimi' },
    { title: 'API Entegrasyonu', desc: 'Banka ve ödeme sistemleri entegrasyonu' },
    { title: 'Mobil Uyumlu', desc: 'Tüm cihazlarda sorunsuz deneyim' }
  ];

  const screenshots = [
    { image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop', title: 'Ana Dashboard' },
    { image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop', title: 'Analitik Görünümü' },
    { image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&h=500&fit=crop', title: 'Raporlar' }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/?scrollTo=portfolio"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Portfolyoya Dön
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm font-medium rounded-full">
                  {project.category}
                </span>
                <span className="text-gray-500">{project.year}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {project.title}
              </h1>

              <p className="text-xl text-gray-400 mb-8">{project.description}</p>

              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <span className="text-gray-500">Müşteri</span>
                  <p className="text-white font-medium">{project.client}</p>
                </div>
                <div>
                  <span className="text-gray-500">Süre</span>
                  <p className="text-white font-medium">{project.duration}</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src={project.heroImage}
                alt={project.title}
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl p-6 shadow-xl">
                <Code className="w-8 h-8 text-white mb-2" />
                <div className="text-white font-bold text-2xl">45+</div>
                <div className="text-blue-100 text-sm">API Endpoints</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 text-center hover:border-blue-500/50 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Zorluk</h3>
              <p className="text-gray-400">{project.challenge}</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Çözüm</h3>
              <p className="text-gray-400">{project.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Teknoloji <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Stack</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {techStack.map((stack, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 hover:border-blue-500/50 transition-all">
                <h3 className="text-xl font-bold text-white mb-4">{stack.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {stack.techs.map((tech, idx) => (
                    <span key={idx} className="px-3 py-1 bg-slate-700/50 text-gray-300 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Özellikler <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">& Modüller</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 hover:border-blue-500/50 transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/30 transition-colors">
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">{feature.title}</h3>
                    <p className="text-gray-400 text-sm">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Ekran <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Görüntüleri</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {screenshots.map((screen, index) => (
              <div key={index} className="group relative overflow-hidden rounded-2xl">
                <img
                  src={screen.image}
                  alt={screen.title}
                  className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-6">
                  <span className="text-white font-medium">{screen.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Web Projenizi
            <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent"> Hayata Geçirelim</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Modern ve performanslı web uygulamaları için bizimle iletişime geçin.
          </p>
          <Button 
            onClick={() => window.location.href = '/iletisim'}
            className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-8 py-6 text-lg rounded-xl"
          >
            Proje Görüşmesi Talep Et
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebPortfolio;
